import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { ErrorBoundary } from '@/components/errorBoundary';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { FinancialPositionReadiness } from '@/api/models';
import { OVERVIEW_ERROR_KEYS } from '../overview';
import { useZoneRail } from './useZoneRail';
import { ZoneRailView } from './ZoneRailView';

/**
 * The assessment rail, self-served like the four metric cards below it: its own
 * slice of the Financial Position read, its own `ErrorBoundary`.
 *
 * It shares the query key with them, so the rail and the cards can never
 * describe different moments — and the screen still makes one request.
 */
export const ZoneRail: React.FC = () => {
    const rail = useZoneRail();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t(OVERVIEW_ERROR_KEYS.title),
            errorMessage: t(OVERVIEW_ERROR_KEYS.message),
            errorRetry: t(OVERVIEW_ERROR_KEYS.retry),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="zone-rail-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="zone-rail-error-boundary"
            onRetry={rail.onRetry}
            resetKeys={[rail.error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <ZoneRailContent
                error={rail.error}
                isLoading={rail.isLoading}
                readiness={rail.readiness}
            />
        </ErrorBoundary>
    );
};

type ZoneRailContentProps = {
    error: ErrorResponse | null;
    isLoading: boolean;
    readiness: FinancialPositionReadiness | null;
};

/** Inside the boundary, so the throw below is caught by it. */
const ZoneRailContent: React.FC<ZoneRailContentProps> = ({
    error,
    isLoading,
    readiness,
}) => {
    if (error) {
        throw error;
    }

    if (isLoading || readiness === null) {
        // The zone sequence is fixed content and stays; only the checklist is
        // not known yet. Announcing "bank not connected" before the read lands
        // would be the same confident falsehood an empty list is.
        return <Skeleton className="h-52 w-full rounded-xl" />;
    }

    return <ZoneRailView readiness={readiness} />;
};
